/* istanbul ignore file */
import { z } from 'zod';

const ActivationLockSchema = z.object({
  bypass_code_failed: z.boolean(),
  user_activation_lock_enabled: z.boolean(),
  device_activation_lock_enabled: z.boolean(),
  activation_lock_allowed_while_supervised: z.boolean(),
  activation_lock_supported: z.boolean(),
});

const RecoveryInformationSchema = z.object({
  recovery_lock_enabled: z.boolean(),
  firmware_password_exist: z.boolean(),
  firmware_password_pending: z.boolean(),
  password_rotation_scheduled: z.nullable(z.string()),
  password_has_been_set: z.boolean(),
});

const FileVaultSchema = z.object({
  filevault_enabled: z.nullable(z.boolean()),
  filevault_recoverykey_type: z.string(),
  filevault_prk_escrowed: z.boolean(),
  filevault_next_rotation: z.string(),
  filevault_regen_required: z.boolean(),
});

const LastApnsRequestSchema = z.object({
  status: z.number(),
  requested_at: z.string(),
  reason: z.nullable(z.string()),
  description: z.nullable(z.string()),
});

const DeviceSchema = z.object({
  install_date: z.string(),
  last_check: z.string(),
  has_unlock_token: z.boolean(),
  has_bootstrap_token: z.boolean(),
  abm_data: z.nullable(z.string()),
  is_certificate_expired: z.boolean(),
  last_apns_request: LastApnsRequestSchema,
});

const MDMOptionsSchema = z.object({
  BootstrapTokenAllowed: z.nullable(z.boolean()),
  ActivationLockAllowedWhileSupervised: z.boolean(),
});

const ServiceSubscriptionSchema = z.object({
  EID: z.nullable(z.string()),
  IMEI: z.string(),
  MEID: z.string(),
  Slot: z.string(),
  ICCID: z.nullable(z.string()),
  Label: z.nullable(z.string()),
  LabelID: z.nullable(z.string()),
  IsRoaming: z.nullable(z.boolean()),
  CurrentMCC: z.nullable(z.string()),
  CurrentMNC: z.nullable(z.string()),
  PhoneNumber: z.nullable(z.string()),
  IsDataPreferred: z.nullable(z.boolean()),
  IsVoicePreferred: z.nullable(z.boolean()),
  CurrentCarrierNetwork: z.nullable(z.string()),
  CarrierSettingsVersion: z.nullable(z.string()),
});

const MDMInfoSchema = z.object({
  IMEI: z.string(),
  MEID: z.string(),
  UDID: z.string(),
  ICCID: z.nullable(z.string()),
  Model: z.string(),
  SIMMCC: z.nullable(z.string()),
  SIMMNC: z.nullable(z.string()),
  UserID: z.nullable(z.string()),
  WiFiMAC: z.string(),
  DeviceID: z.nullable(z.string()),
  HostName: z.nullable(z.string()),
  TimeZone: z.string(),
  IsRoaming: z.boolean(),
  ModelName: z.string(),
  OSVersion: z.string(),
  PushToken: z.nullable(z.string()),
  QuotaSize: z.nullable(z.string()),
  CurrentMCC: z.nullable(z.string()),
  CurrentMNC: z.nullable(z.string()),
  DeviceName: z.string(),
  MDMOptions: MDMOptionsSchema,
  EthernetMAC: z.nullable(z.string()),
  IsMultiUser: z.boolean(),
  PhoneNumber: z.nullable(z.string()),
  ProductName: z.string(),
  BatteryLevel: z.number(),
  BluetoothMAC: z.string(),
  BuildVersion: z.string(),
  EthernetMACs: z.nullable(z.string()),
  IsSupervised: z.boolean(),
  NotOnConsole: z.nullable(z.string()),
  SerialNumber: z.string(),
  UserLongName: z.nullable(z.string()),
  LocalHostName: z.nullable(z.string()),
  ResidentUsers: z.nullable(z.string()),
  SubscriberMCC: z.string(),
  SubscriberMNC: z.string(),
  UserShortName: z.nullable(z.string()),
  DeviceCapacity: z.number(),
  IsAppleSilicon: z.nullable(z.boolean()),
  OSUpdateSettings: z.nullable(z.string()),
  OrganizationInfo: z.nullable(z.string()),
  IsNetworkTethered: z.boolean(),
  SIMCarrierNetwork: z.nullable(z.string()),
  ActiveManagedUsers: z.nullable(z.string()),
  CellularTechnology: z.number(),
  DataRoamingEnabled: z.boolean(),
  AppAnalyticsEnabled: z.boolean(),
  EASDeviceIdentifier: z.string(),
  LastCloudBackupDate: z.nullable(z.string()),
  VoiceRoamingEnabled: z.boolean(),
  IsCloudBackupEnabled: z.boolean(),
  IsMDMLostModeEnabled: z.boolean(),
  MaximumResidentUsers: z.nullable(z.string()),
  ModemFirmwareVersion: z.string(),
  ServiceSubscriptions: z.array(ServiceSubscriptionSchema),
  AwaitingConfiguration: z.boolean(),
  CurrentCarrierNetwork: z.nullable(z.string()),
  AutoSetupAdminAccounts: z.nullable(z.string()),
  CarrierSettingsVersion: z.nullable(z.string()),
  EstimatedResidentUsers: z.nullable(z.string()),
  IsDoNotDisturbInEffect: z.boolean(),
  PersonalHotspotEnabled: z.boolean(),
  SupportsiOSAppInstalls: z.nullable(z.boolean()),
  iTunesStoreAccountHash: z.nullable(z.string()),
  AvailableDeviceCapacity: z.number(),
  IsActivationLockEnabled: z.boolean(),
  PINRequiredForDeviceLock: z.nullable(z.boolean()),
  SubscriberCarrierNetwork: z.nullable(z.string()),
  SupplementalBuildVersion: z.string(),
  CurrentConsoleManagedUser: z.nullable(z.string()),
  IsActivationLockSupported: z.nullable(z.boolean()),
  PINRequiredForEraseDevice: z.nullable(z.boolean()),
  SupplementalOSVersionExtra: z.nullable(z.string()),
  iTunesStoreAccountIsActive: z.boolean(),
  DiagnosticSubmissionEnabled: z.boolean(),
  IsDeviceLocatorServiceEnabled: z.boolean(),
  SystemIntegrityProtectionEnabled: z.nullable(z.boolean()),
  setWallpaper: z.object({
    Where: z.array(z.number()),
  }),
  setMDMOptions: z.object({
    BootstrapTokenAllowed: z.boolean(),
    ActivationLockAllowedWhileSupervised: z.boolean(),
  }),
});

const MDMProfilePayloadContentSchema = z.object({
  PayloadType: z.string(),
  PayloadUUID: z.string(),
  PayloadVersion: z.number(),
  PayloadIdentifier: z.string(),
  PayloadDescription: z.nullable(z.string()),
  PayloadDisplayName: z.nullable(z.string()),
  PayloadOrganization: z.nullable(z.string()),
});

const MDMProfileSchema = z.object({
  IsManaged: z.boolean(),
  IsEncrypted: z.boolean(),
  PayloadUUID: z.string(),
  PayloadContent: z.array(MDMProfilePayloadContentSchema),
  PayloadVersion: z.number(),
  PayloadIdentifier: z.string(),
  HasRemovalPasscode: z.boolean(),
  PayloadDescription: z.nullable(z.string()),
  PayloadDisplayName: z.nullable(z.string()),
  SignerCertificates: z.nullable(z.array(z.string())),
  PayloadOrganization: z.nullable(z.string()),
  PayloadRemovalDisallowed: z.boolean(),
});

const MDMCertificateSchema = z.object({
  Data: z.string(),
  CommonName: z.string(),
  IsIdentity: z.boolean(),
});

const ManagementStatusSchema = z.object({
  EnrolledViaDEP: z.nullable(z.boolean()),
  IsUserEnrollment: z.boolean(),
  UserApprovedEnrollment: z.nullable(z.boolean()),
  IsActivationLockManageable: z.nullable(z.boolean()),
});

const MDMSecurityInfoSchema = z.object({
  SecureBoot: z.nullable(z.boolean()),
  FDE_Enabled: z.nullable(z.boolean()),
  PasscodePresent: z.boolean(),
  FirewallSettings: z.nullable(z.string()),
  ManagementStatus: ManagementStatusSchema,
  PasscodeCompliant: z.boolean(),
  RemoteDesktopEnabled: z.nullable(z.boolean()),
  FirmwarePasswordStatus: z.nullable(z.string()),
  HardwareEncryptionCaps: z.number(),
  PasscodeLockGracePeriod: z.number(),
  FDE_HasPersonalRecoveryKey: z.nullable(z.boolean()),
  FDE_PersonalRecoveryKeyCMS: z.nullable(z.string()),
  PasscodeCompliantWithProfiles: z.boolean(),
  AuthenticatedRootVolumeEnabled: z.nullable(z.boolean()),
  FDE_HasInstitutionalRecoveryKey: z.nullable(z.boolean()),
  PasscodeLockGracePeriodEnforced: z.number(),
  FDE_PersonalRecoveryKeyDeviceKey: z.nullable(z.string()),
  SystemIntegrityProtectionEnabled: z.nullable(z.boolean()),
  BootstrapTokenRequiredForSoftwareUpdate: z.nullable(z.boolean()),
  BootstrapTokenRequiredForKernelExtensionApproval: z.nullable(z.boolean()),
});

const ThreatCountsSchema = z.object({
  malware_count: z.number(),
  pup_count: z.number(),
  total: z.number(),
  not_quarantined_total: z.number(),
  malicious_detected_count: z.number(),
});

const SecurityInformationSchema = z.object({
  remote_desktop_enabled: z.nullable(z.boolean()),
});

export const AppleDeviceDetailRootSchema = z.object({
  id: z.string(),
  status: z.string(),
  hardware_uuid: z.nullable(z.string()),
  first_report: z.nullable(z.string()),
  info: z.nullable(z.string()),
  asset_tag: z.string(),
  deferred_install: z.boolean(),
  blueprint_id: z.string(),
  enrolled_at: z.string(),
  is_missing: z.boolean(),
  activation_lock: ActivationLockSchema,
  recovery_information: RecoveryInformationSchema,
  lost_mode: z.nullable(z.string()),
  filevault: FileVaultSchema,
  duplicates: z.array(z.unknown()),
  serial_number: z.string(),
  name: z.string(),
  model: z.string(),
  has_file_vault_prk: z.boolean(),
  agent_version: z.nullable(z.string()),
  first_enrolled_at: z.string(),
  user: z.nullable(z.string()),
  is_mdm: z.boolean(),
  lock_status: z.string(),
  erase_status: z.string(),
  is_enrollment_finished: z.boolean(),
  device: DeviceSchema,
  agent_installed_at: z.nullable(z.string()),
  last_seen_dt: z.string(),
  os_version: z.string(),
  supplemental_build_version: z.string(),
  supplemental_os_version_extra: z.nullable(z.string()),
  device_users_list: z.array(z.unknown()),
  device_family: z.string(),
  udid: z.string(),
  mdm_info: MDMInfoSchema,
  build_version: z.string(),
  mdm_os_updates: z.array(
    z.object({
      Build: z.string(),
      Version: z.string(),
      IsCritical: z.boolean(),
      ProductKey: z.string(),
      InstallSize: z.number(),
      MetadataURL: z.nullable(z.string()),
      ProductName: z.string(),
      DownloadSize: z.number(),
      DeferredUntil: z.nullable(z.string()),
      IsMajorOSUpdate: z.nullable(z.boolean()),
      RestartRequired: z.boolean(),
      IsFirmwareUpdate: z.nullable(z.boolean()),
      HumanReadableName: z.string(),
      AllowsInstallLater: z.boolean(),
      IsConfigDataUpdate: z.nullable(z.boolean()),
      IsSecurityResponse: z.boolean(),
      AppIdentifiersToClose: z.nullable(z.string()),
      RequiresBootstrapToken: z.nullable(z.boolean()),
      HumanReadableNameLocale: z.nullable(z.string()),
      SupplementalBuildVersion: z.string(),
    }),
  ),
  mdm_profile_list: z.array(MDMProfileSchema),
  mdm_certificate_list: z.array(MDMCertificateSchema),
  mdm_security_info: MDMSecurityInfoSchema,
  mdm_applications_updated_at: z.string(),
  mdm_certificate_list_updated_at: z.string(),
  mdm_info_updated_at: z.string(),
  mdm_os_updates_updated_at: z.string(),
  mdm_profile_list_updated_at: z.string(),
  mdm_security_info_updated_at: z.string(),
  enrollment_type: z.number(),
  is_supervised: z.boolean(),
  number_of_vulnerabilities: z.nullable(z.number()),
  highest_vulnerability_score: z.nullable(z.number()),
  highest_severity: z.nullable(z.string()),
  severity_counts: z.object({}),
  threat_counts: ThreatCountsSchema,
  security_information: SecurityInformationSchema,
  tags: z.array(z.string()),
  battery_health: z.nullable(z.string()),
});
